import {
  initializePaddle,
  type Paddle,
  type Environments,
} from '@paddle/paddle-js';
import type { CheckoutDescriptor } from '@/types/checkout';
import { tbankService } from '@/shared/api/services/tbank.service';
import { isWebPaymentsEnabled } from './web-payments';

/**
 * Paddle.js bootstrap for the WEB pack checkout (the sole web payment rail).
 *
 * Paddle is Merchant of Record: it renders the checkout overlay, collects
 * payment + tax, and the API webhook (POST /v1/webhooks/paddle) is the source
 * of truth that grants the Facade Kit. This module only opens the overlay and
 * plants the correlation token; it never interprets payment state itself
 * (API-driven state rule). The `checkout.completed` event is a UX accelerator
 * that refetches /projects — the webhook still owns the grant.
 *
 * Config comes from NEXT_PUBLIC_* (exposed to the browser):
 *  - NEXT_PUBLIC_PADDLE_CLIENT_TOKEN  client-side token (`test_` prefix in sandbox)
 *  - NEXT_PUBLIC_PADDLE_ENV           'sandbox' | 'production'
 */

/** sessionStorage key holding the in-app path a T-Bank checkout started from —
 *  written right before the full-page redirect to the bank, read back by
 *  /payment/tbank/return to land the buyer on the screen they left. */
export const TBANK_RETURN_TO_KEY = 'tbank:return_to';

const CLIENT_TOKEN = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN;
const ENVIRONMENT = (process.env.NEXT_PUBLIC_PADDLE_ENV ||
  'sandbox') as Environments;

/**
 * Whether the Paddle.js client is bootstrappable at all (flag on + client
 * token). Every product — pack included — carries its own price id in the API
 * `checkout` descriptor, so no price env var is involved: a null descriptor
 * (region not served) disables the buy control per product.
 */
export function isPaddleClientConfigured(): boolean {
  return isWebPaymentsEnabled && !!CLIENT_TOKEN;
}

/**
 * Map an app locale (our 24 `messages/<locale>` codes) to a locale the Paddle
 * checkout overlay understands. Paddle supports only a subset; codes it doesn't
 * carry (cs, fi, he, hi, id, ro, uk) map to `undefined` so the overlay falls
 * back to the buyer's browser language instead of hard-defaulting to English.
 * Two codes differ from ours: Norwegian Bokmål `nb` → Paddle `no`, and Chinese
 * `zh` → Simplified `zh-Hans`.
 */
const PADDLE_LOCALE_BY_APP_LOCALE: Record<string, string> = {
  ar: 'ar',
  da: 'da',
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  nb: 'no',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  ru: 'ru',
  sv: 'sv',
  tr: 'tr',
  zh: 'zh-Hans',
};

export function toPaddleLocale(appLocale: string): string | undefined {
  return PADDLE_LOCALE_BY_APP_LOCALE[appLocale];
}

// The SDK delivers checkout events through the init-time eventCallback, so we
// install ONE global callback and fan the per-open listeners out. `completed`
// fires once on a successful payment; `closed` fires when the overlay is
// dismissed (after a purchase OR a plain cancel). Both are one-shot per open()
// — the Kit grant and the host-dialog restore are per-checkout.
const completionListeners = new Set<() => void>();
const closeListeners = new Set<() => void>();
let paddlePromise: Promise<Paddle | undefined> | null = null;

function getPaddle(): Promise<Paddle | undefined> {
  if (!CLIENT_TOKEN) {
    return Promise.resolve(undefined);
  }

  // Memoize: Paddle.js must be initialized once per page load.
  paddlePromise ??= initializePaddle({
    token: CLIENT_TOKEN,
    environment: ENVIRONMENT,
    eventCallback: (event) => {
      if (event?.name === 'checkout.completed') {
        completionListeners.forEach((listener) => listener());
        completionListeners.clear();
      } else if (event?.name === 'checkout.closed') {
        // Overlay dismissed — let the host restore its modal lock / resting
        // state. Fires after both a completed purchase and a cancel.
        closeListeners.forEach((listener) => listener());
        closeListeners.clear();
      }
    },
  });

  return paddlePromise;
}

/**
 * Checkout kind planted flat in Paddle `customData.kind` so the API webhook
 * routes the completed transaction to the right grant path (pack / pass /
 * subscription / token). Mirrors PurchaseIntentKindEnum on the server.
 */
export type CheckoutKind = 'pack' | 'pass' | 'subscription' | 'token';

export interface OpenCheckoutArgs {
  /** Paddle price id (`pri_…`) — from the API `checkout.reference` descriptor
   *  for passes/subscriptions/tokens, or the pack env price id. */
  priceId: string;
  /** What is being bought — lands in customData so the webhook's kind-aware
   *  guard fires the matching grant. */
  kind: CheckoutKind;
  /** Opaque correlation token from POST /v1/purchases/intents — binds the
   *  webhook to this buyer (+ building for pack). Planted flat in customData. */
  correlationToken: string;
  customerEmail?: string;
  countryCode?: string;
  locale?: string;
  onCompleted?: () => void;
  onClosed?: () => void;
}

/**
 * Open the Paddle checkout overlay for any product kind. `customData` is kept
 * FLAT (Paddle discourages nested objects) and carries `kind` + the correlation
 * token so the API's kind-aware transaction.completed guard grants the right
 * product. The webhook remains the source of truth (API-driven state); the
 * `checkout.completed` event only accelerates the local UI refresh.
 */
export async function openCheckout({
  priceId,
  kind,
  correlationToken,
  customerEmail,
  countryCode,
  locale,
  onCompleted,
  onClosed,
}: OpenCheckoutArgs): Promise<void> {
  const paddle = await getPaddle();
  if (!paddle) {
    throw new Error('Paddle.js failed to initialize');
  }

  if (onCompleted) {
    completionListeners.add(onCompleted);
  }
  if (onClosed) {
    closeListeners.add(onClosed);
  }

  const customer = customerEmail
    ? {
        email: customerEmail,
        ...(countryCode ? { address: { countryCode } } : {}),
      }
    : undefined;

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customData: { correlation_token: correlationToken, kind },
    settings: { displayMode: 'overlay', ...(locale ? { locale } : {}) },
    ...(customer ? { customer } : {}),
  });
}

/**
 * Whether the buy CTA can open a checkout for this descriptor. Provider-aware:
 * Paddle needs its client SDK configured; T-Bank is a server-driven full-page
 * redirect and only needs web payments enabled. A null descriptor (country not
 * served on any web rail) always disables the CTA — the card then shows the
 * server `web_payment_notice`.
 */
export function isCheckoutAvailableForDescriptor(
  descriptor: CheckoutDescriptor | null | undefined,
): boolean {
  if (!descriptor) {
    return false;
  }
  if (descriptor.provider === 'paddle') {
    return isPaddleClientConfigured();
  }
  if (descriptor.provider === 'tbank') {
    return isWebPaymentsEnabled;
  }
  return false;
}

export interface DescriptorCheckoutArgs
  extends Omit<OpenCheckoutArgs, 'priceId' | 'correlationToken'> {
  /** Opaque token from POST /v1/purchases/intents — REQUIRED for Paddle.
   *  Unused for tbank: its checkout-session endpoint mints the intent
   *  server-side. */
  correlationToken?: string;
  /** Building id — required for the tbank pack session (building-bound kind). */
  projectId?: string;
}

/**
 * Provider-agnostic entry point: open a checkout from the API `checkout`
 * descriptor. Routes by `descriptor.provider` — Paddle (overlay) or T-Bank
 * (full-page redirect to the bank-hosted form). Every storefront CTA goes
 * through here, so a new provider arm lands with no per-CTA change.
 */
export async function openCheckoutForDescriptor(
  descriptor: CheckoutDescriptor,
  args: DescriptorCheckoutArgs,
): Promise<void> {
  if (descriptor.provider === 'tbank') {
    // T-Bank: descriptor.reference is the catalog SLUG. The server mints the
    // intent, calls Init with the server-owned RUB price, and returns the
    // bank's hosted-form URL. Full-page redirect — the Paddle-specific args
    // (customData, overlay listeners) do not apply; the buyer comes back via
    // /payment/tbank/return, and the notification webhook owns the grant.
    const session = await tbankService.createSession(
      descriptor.reference,
      args.projectId,
    );
    // Defensive: a malformed/empty success response (e.g. a proxy stripping
    // the body) must surface as a thrown Error the CTA can toast, not as a
    // TypeError that dies silently in the promise chain.
    const paymentUrl = session?.attributes?.payment_url;
    if (!paymentUrl) {
      throw new Error('T-Bank checkout session returned no payment URL');
    }
    // Remember where checkout started so the return page can send the buyer
    // back to the exact screen (concept, storefront, …) instead of the home
    // page. sessionStorage survives the same-tab round-trip through the bank
    // and stays behind if the bank redirect lands in a different tab/browser
    // (the return page then falls back to "/").
    try {
      sessionStorage.setItem(
        TBANK_RETURN_TO_KEY,
        window.location.pathname + window.location.search,
      );
    } catch {
      // Storage may be unavailable (private mode quotas) — losing the
      // return-to nicety must never block the payment redirect.
    }
    window.location.assign(paymentUrl);
    return;
  }

  if (descriptor.provider !== 'paddle') {
    throw new Error(`Unsupported checkout provider: ${descriptor.provider}`);
  }

  const { correlationToken, projectId: _projectId, ...rest } = args;
  if (!correlationToken) {
    throw new Error('Paddle checkout requires a correlation token');
  }

  return openCheckout({
    ...rest,
    correlationToken,
    priceId: descriptor.reference,
  });
}

export interface OpenPackCheckoutArgs {
  /** Paddle price id of the Facade Kit one-time charge. */
  priceId: string;
  /** Opaque correlation token from POST /v1/purchases/intents — binds the
   *  webhook to this buyer + building. Planted flat in customData. */
  correlationToken: string;
  /** Pre-fill the buyer email when known. Together with `countryCode`, Paddle
   *  skips its "Your details" step and lands the buyer straight on payment. */
  customerEmail?: string;
  /** Buyer country as ISO 3166-1 alpha-2 (e.g. `MD`, `US`), from the live geo
   *  signal. Prefills Paddle's country (tax + payment-method availability). */
  countryCode?: string;
  /** Paddle checkout UI language (already mapped via `toPaddleLocale`). Omit to
   *  let Paddle fall back to the buyer's browser locale. */
  locale?: string;
  /** Fired on the `checkout.completed` event — a UX accelerator to refetch
   *  /projects so the Kit tag flips without waiting on the webhook round-trip. */
  onCompleted?: () => void;
  /** Fired on the `checkout.closed` event (overlay dismissed, after a purchase
   *  OR a cancel) — lets the host restore its resting state (e.g. re-lock the
   *  paywall dialog it relaxed to make this overlay clickable). */
  onClosed?: () => void;
}

/**
 * Open the Paddle checkout overlay for a one-time Facade Kit purchase.
 *
 * `customData` is kept FLAT (Paddle discourages nested objects) and carries
 * `kind: 'pack'` so the API's transaction.completed one-time guard fires.
 */
export async function openPackCheckout(
  args: OpenPackCheckoutArgs,
): Promise<void> {
  return openCheckout({ ...args, kind: 'pack' });
}
